import { businessPartnersApi, itemsApi } from '@/api';
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';

function clearAuthAndRedirectToLogout() {
  document.cookie = '_auth=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
  window.location.href = '/auth/logout';
}

export const useCreateQueryClient = () => {
  const { enqueueSnackbar } = useSnackbar();

  const errorHandler = (error: unknown) => {
    if (error instanceof AxiosError) {
      if (error.status === 403) {
        console.error(error);
        enqueueSnackbar(
          'Du hast keine Berechtigung, diese Aktion auszuführen.',
          {
            variant: 'error',
          },
        );
      } else if (error.status === 401) {
        // Remove auth cookie and redirect to logout page
        clearAuthAndRedirectToLogout();
        enqueueSnackbar(
          'Du hast keine Berechtigung, diese Aktion auszuführen. Bitte logge dich erneut ein.',
          {
            variant: 'error',
          },
        );
      } else if (error.status === 400) {
        enqueueSnackbar(error?.response?.data?.message, {
          variant: 'error',
        });
      } else if (error.status === 409) {
        enqueueSnackbar(error?.response?.data?.message, {
          variant: 'error',
        });
      } else {
        enqueueSnackbar(
          'Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.',
          {
            variant: 'error',
          },
        );
      }
    } else {
      enqueueSnackbar(
        'Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.',
        {
          variant: 'error',
        },
      );
    }
  };

  const queryCache = new QueryCache({
    onError: errorHandler,
  });

  const mutationCache = new MutationCache({
    onError: errorHandler,
  });

  return new QueryClient({
    queryCache,
    mutationCache,
    defaultOptions: {
      queries: {
        refetchOnMount: true,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          return false;
          // if (error instanceof AxiosError && error.status === 401) {
          //   return false; // Don't retry on 401 unauthorized
          // }
          // return failureCount < 3; // Default retry behavior for other errors
        },
      },
      mutations: {
        retry: (failureCount, error) => {
          return false;
          // if (error instanceof AxiosError && error.status === 401) {
          //   return false; // Don't retry on 401 unauthorized
          // }
          // return failureCount < 3; // Default retry behavior for other errors
        },
      },
    },
  });
};

export const createQueryClient = () => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnMount: true,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          return false;
        },
      },
      mutations: {
        retry: (failureCount, error) => {
          return false;
        },
      },
    },
  });
};

export const queryKeys = {
  businessPartnerItems: (businessPartnerId: number) => [
    'businessPartnerItems',
    businessPartnerId,
  ],
};

export const queryCache = {
  prefetchBusinessPartnerItems: async (
    queryClient: QueryClient,
    businessPartnerId: number,
  ) => {
    const queryKey = queryKeys.businessPartnerItems(businessPartnerId);
    if (!queryClient.getQueryData(queryKey)) {
      await queryClient.prefetchQuery({
        queryKey,
        queryFn: () =>
          businessPartnersApi
            .getItemsByBusinessPartnerId(businessPartnerId)
            .then((res) => res.data),
      });
    }
  },
  prefetchBusinessPartner: async (
    queryClient: QueryClient,
    businessPartnerId: number,
  ) => {
    const queryKey = ['businessPartner', businessPartnerId];
    if (!queryClient.getQueryData(queryKey)) {
      await queryClient.prefetchQuery({
        queryKey,
        queryFn: () =>
          businessPartnersApi
            .getBusinessPartnerById(businessPartnerId)
            .then((res) => res.data),
      });
    }
  },
  prefetchItemFacets: async (queryClient: QueryClient) => {
    const queryKey = ['itemFacets', undefined];
    if (!queryClient.getQueryData(queryKey)) {
      await queryClient.prefetchQuery({
        queryKey,
        queryFn: () => itemsApi.getItemFacets().then((res) => res.data),
      });
    }
  },
  prefetchSalesPerson: async (
    queryClient: QueryClient,
    businessPartnerId: number,
  ) => {
    const queryKey = ['sales-person', businessPartnerId];
    if (!queryClient.getQueryData(queryKey)) {
      await queryClient.prefetchQuery({
        queryKey,
        queryFn: () =>
          businessPartnersApi
            .getSalesPersonByBusinessPartnerId(businessPartnerId)
            .then((res) => res.data),
      });
    }
  },
};
